import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useConnectedAccounts, useDeals, useStakeDebit, useUnclaimedTransactions } from "../../CustomHooks/Queries";
import useConnectBankAccount from "../../CustomHooks/useConnectBankAccount";
import renderGetBankAmountDetails from "../../Organisms/Modals/Add Funds/get-bank-amount-details";
import renderGetRecurringDepositPreferences from "../../Organisms/Modals/Recurring Deposits/get-recurring-deposit-preferences";
import renderGetRecurringDepositAmountDetails from "../../Organisms/Modals/Add Funds/get-recurring-deposit-amount-details";
import renderDirectDepositSetup from "../../Organisms/Modals/Direct Deposit/direct-deposit-setup";
import {
  MemberSetupMilestoneProgress,
  ADD_FUNDS_PHASE_MILESTONE,
  TRANSFER_CONFIRMED_MILESTONE,
  SKIP_DIRECT_DEPOSIT_MILESTONE,
} from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";
import MemberSetupAddFundsHub from "./member-setup-add-funds-hub";
import MemberSetupTransferConfirmed from "./member-setup-transfer-confirmed";
import MemberSetupSkipDirectDeposit from "./member-setup-skip-direct-deposit";
import BattlecardHeaderLayout from "./battlecard-header-layout";
import { APIQueryKeys } from "../../Utils/constants";
import {
  getUnclaimedCashBackAmountNumeric,
  isSVDEligible,
  BATTLECARD_FLOW_NAV_STATE,
  BATTLECARD_LEGACY_ONLY_STATE,
  isSVDVerificationPendingStatus,
  isSVDVerificationRejected,
  isUnlimitedMoveInCashPlanFromDealsResponse,
  isBasicMoveInCashPlanFromDealsResponse,
  formatBasicSpendingQualifierForRebateFromDealsResponse,
} from "../../Utils/helpers";
import "./battle-cards.scss";

const ADD_FUNDS_PAGE_CLASS = "battle-cards-page bc-member-setup-page bc-member-setup-page--add-funds";

/**
 * Paper 76Q-0 — Add Funds hub (battle card member setup).
 */
export default function MemberSetupAddFunds() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const modalContext = useModalContext();
  const { setAppUtilityProps } = useAppUtilityContext();

  const { data: dealsData } = useDeals();
  const { isFetching: isFetchingStakeDebit, data: stakeDebitData } = useStakeDebit();
  const { data: connectedAccountsData } = useConnectedAccounts();
  const { data: unclaimedTransactionsData } = useUnclaimedTransactions();

  const [transferConfirmedDetails, setTransferConfirmedDetails] = useState(null);
  const [showSkipDirectDepositConfirm, setShowSkipDirectDepositConfirm] = useState(false);
  const plaidFollowUpRef = useRef(null);

  useEffect(() => {
    setAppUtilityProps((p) => ({ ...p, hideNavigationBar: true, lockedRoutes: ["/deals", "/cash"] }));
    return () => setAppUtilityProps((p) => ({ ...p, lockedRoutes: [] }));
  }, [setAppUtilityProps]);

  useEffect(() => {
    if (!isFetchingStakeDebit && stakeDebitData) {
      if (isSVDVerificationRejected(stakeDebitData?.data)) {
        navigate("/battlecard", {
          replace: true,
          state: { ...BATTLECARD_LEGACY_ONLY_STATE, ...BATTLECARD_FLOW_NAV_STATE },
        });
        return;
      }
      if (isSVDVerificationPendingStatus(stakeDebitData?.data)) {
        navigate("/battlecard/member-setup", { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
        return;
      }
      if (isSVDEligible(stakeDebitData?.data)) {
        const progressState = dealsData?.data?.data?.currentDeal?.stake_checking_progress_state ?? 0;
        if (progressState < 7) {
          navigate("/battlecard/member-setup", { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
        }
      }
    }
  }, [isFetchingStakeDebit, stakeDebitData, dealsData, navigate]);

  const isBankConnected = (connectedAccountsData?.data?.data?.accounts?.length ?? 0) > 0;
  const monthlyCashback = dealsData?.data?.data?.currentDeal?.stake_pay?.stake_checking?.cashback_amount ?? 1000;
  const cashbackBonusDollars = Math.round(monthlyCashback / 100);
  const directDepositBonusDollars = useMemo(() => {
    const amount = getUnclaimedCashBackAmountNumeric(unclaimedTransactionsData);
    if (amount > 0) {
      return amount >= 100 ? Math.round(amount / 100) : Math.round(amount);
    }
    return 200;
  }, [unclaimedTransactionsData]);

  const showDirectDepositSection = useMemo(
    () => isUnlimitedMoveInCashPlanFromDealsResponse(dealsData),
    [dealsData],
  );

  const isBasicPlan = useMemo(
    () => isBasicMoveInCashPlanFromDealsResponse(dealsData),
    [dealsData],
  );

  const basicSpendingQualifier = useMemo(
    () => formatBasicSpendingQualifierForRebateFromDealsResponse(dealsData),
    [dealsData],
  );

  const refetchAfterFunding = useCallback(() => {
    queryClient.refetchQueries(APIQueryKeys.stakeDebit);
    queryClient.refetchQueries(APIQueryKeys.connectedAccounts);
  }, [queryClient]);

  const handleDirectDeposit = useCallback(() => {
    renderDirectDepositSetup(modalContext, {
      onSuccess: () => {
        refetchAfterFunding();
        window.location.assign("/home");
      },
    });
  }, [modalContext, refetchAfterFunding]);

  const { openPlaid } = useConnectBankAccount({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.connectedAccounts);
      const followUp = plaidFollowUpRef.current;
      plaidFollowUpRef.current = null;
      followUp?.();
    },
  });

  const runAfterBankConnected = useCallback(
    (followUp) => {
      if (isBankConnected) {
        followUp();
      } else {
        plaidFollowUpRef.current = followUp;
        openPlaid();
      }
    },
    [isBankConnected, openPlaid],
  );

  const handleTransferConfirmed = useCallback(
    (details) => {
      refetchAfterFunding();
      setTransferConfirmedDetails({
        amount: details.amount ?? details.cashAmount,
        fromLabel: details.fromLabel,
        toLabel: details.toLabel ?? "Stake Checking",
        estimatedArrival: details.estimatedArrival ?? "1–3 business days",
      });
    },
    [refetchAfterFunding],
  );

  const openOneTimeDepositFlow = useCallback(() => {
    renderGetBankAmountDetails(modalContext, {
      onTransferConfirmed: handleTransferConfirmed,
      onSuccess: refetchAfterFunding,
    });
  }, [modalContext, handleTransferConfirmed, refetchAfterFunding]);

  const openRecurringDepositFlow = useCallback(() => {
    renderGetRecurringDepositPreferences(modalContext, {
      onSubmit: ({ startDate, frequency }) =>
        renderGetRecurringDepositAmountDetails(modalContext, {
          recurringDepositPreferences: { startDate, frequency },
          onFormSubmit: () => refetchAfterFunding(),
        }),
    });
  }, [modalContext, refetchAfterFunding]);

  const handleTransferConfirmedContinue = useCallback(() => {
    window.location.assign("/home");
  }, []);

  const handleFundLater = useCallback(() => {
    setShowSkipDirectDepositConfirm(true);
  }, []);

  const handleSkipDirectDepositGoBack = useCallback(() => {
    setShowSkipDirectDepositConfirm(false);
  }, []);

  const handleSkipDirectDepositConfirm = useCallback(() => {
    setShowSkipDirectDepositConfirm(false);
    window.location.assign("/home");
  }, []);

  let pageContent;

  if (isFetchingStakeDebit) {
    pageContent = (
      <div className={ADD_FUNDS_PAGE_CLASS}>
        <div className="bc-member-setup-inner">
          <div className="bc-member-setup-header bc-member-setup-header--no-back">
            <span className="bc-member-setup-title">Become a Member</span>
          </div>
        </div>
      </div>
    );
  } else if (transferConfirmedDetails) {
    pageContent = (
      <div className={ADD_FUNDS_PAGE_CLASS}>
        <div className="bc-member-setup-inner">
          <div className="bc-member-setup-header bc-member-setup-header--no-back">
            <span className="bc-member-setup-title">Become a Member</span>
          </div>
          <MemberSetupMilestoneProgress milestone={TRANSFER_CONFIRMED_MILESTONE} />
          <div className="bc-member-setup-divider" />
          <MemberSetupTransferConfirmed
            {...transferConfirmedDetails}
            onContinue={handleTransferConfirmedContinue}
          />
        </div>
      </div>
    );
  } else if (showSkipDirectDepositConfirm) {
    pageContent = (
      <div className={ADD_FUNDS_PAGE_CLASS}>
        <div className="bc-member-setup-inner">
          <div className="bc-member-setup-header">
            <button
              type="button"
              className="bc-member-setup-back"
              onClick={handleSkipDirectDepositGoBack}
              aria-label="Go back"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M7.5 2.5L4 6L7.5 9.5" stroke="#8B8FA3" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="bc-member-setup-title">Become a Member</span>
          </div>
          <MemberSetupMilestoneProgress milestone={SKIP_DIRECT_DEPOSIT_MILESTONE} />
          <div className="bc-member-setup-divider" />
          <MemberSetupSkipDirectDeposit
            variant={isBasicPlan ? "basic" : "unlimited"}
            instantBonus={cashbackBonusDollars}
            directDepositBonus={directDepositBonusDollars}
            spendingQualifier={basicSpendingQualifier}
            onGoBack={handleSkipDirectDepositGoBack}
            onConfirmSkip={handleSkipDirectDepositConfirm}
          />
        </div>
      </div>
    );
  } else {
    pageContent = (
      <div className={ADD_FUNDS_PAGE_CLASS}>
        <div className="bc-member-setup-inner">
          <div className="bc-member-setup-header bc-member-setup-header--no-back">
            <span className="bc-member-setup-title">Become a Member</span>
          </div>
          <MemberSetupMilestoneProgress milestone={ADD_FUNDS_PHASE_MILESTONE} />
          <div className="bc-member-setup-divider" />
          <MemberSetupAddFundsHub
            cashbackBonus={cashbackBonusDollars}
            showDirectDepositSection={showDirectDepositSection}
            onOneTimeDeposit={() => runAfterBankConnected(openOneTimeDepositFlow)}
            onRecurringDeposit={() => runAfterBankConnected(openRecurringDepositFlow)}
            onDirectDeposit={handleDirectDeposit}
            onFundLater={handleFundLater}
          />
        </div>
      </div>
    );
  }

  return <BattlecardHeaderLayout>{pageContent}</BattlecardHeaderLayout>;
}
